import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { AuthService } from './auth.service';

/** Languages gpt-realtime-translate can listen to (app language codes). */
const LIVE_INPUT_LANGUAGES = new Set([
  'en',
  'ko',
  'zh-CN',
  'ru',
  'kk',
  'uz',
  'fa',
  'es',
]);

/** Languages gpt-realtime-translate can speak and write (app language codes). */
const LIVE_OUTPUT_LANGUAGES = new Set(['en', 'ko', 'zh-CN', 'ru', 'es']);

@Injectable({
  providedIn: 'root',
})
export class LiveTranslationSettingsService {
  constructor(private readonly authService: AuthService) {}

  private readonly storageKey = 'liveTranslationEnabled';
  private readonly liveTranslationSubject = new BehaviorSubject<boolean>(
    this.loadInitialValue()
  );

  /** Live translation is admin-only; for everyone else it reads as off. */
  readonly liveTranslation$: Observable<boolean> = combineLatest([
    this.liveTranslationSubject,
    this.authService.currentUser$,
  ]).pipe(
    map(([enabled]) => enabled && this.authService.isAdmin()),
    distinctUntilChanged()
  );

  isLiveTranslationAvailable(): boolean {
    return this.authService.isAdmin();
  }

  isLiveTranslationEnabled(): boolean {
    return this.liveTranslationSubject.value && this.authService.isAdmin();
  }

  setLiveTranslationEnabled(enabled: boolean): void {
    this.liveTranslationSubject.next(enabled);
    try {
      localStorage.setItem(this.storageKey, String(enabled));
    } catch (error) {
      console.warn(
        '[LiveTranslationSettings] Could not persist setting:',
        error
      );
    }
  }

  isSupportedSourceLanguage(language: string): boolean {
    return LIVE_INPUT_LANGUAGES.has(language);
  }

  isSupportedTargetLanguage(language: string): boolean {
    return LIVE_OUTPUT_LANGUAGES.has(language);
  }

  /** Whether speech in `source` can be live-translated straight into `target`. */
  supportsDirectTranslation(source: string, target: string): boolean {
    return (
      source !== target &&
      this.isSupportedSourceLanguage(source) &&
      this.isSupportedTargetLanguage(target)
    );
  }

  private loadInitialValue(): boolean {
    try {
      return localStorage.getItem(this.storageKey) === 'true';
    } catch {
      return false;
    }
  }
}
